import React, { useContext, useEffect, useRef, useState } from "react";
import "@google/model-viewer";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LayersClearOutlinedIcon from "@mui/icons-material/LayersClearOutlined";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { prefersReducedMotion } from "../design/transitions";
import { useMaterialUIController } from "../../context";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetTitlesCallNoStore } from "../../redux/api/title.api";
import { basicURL } from "../../apiManager/APIConfig";

/** Normalize 3D model URL: relative paths become absolute using API base (so model loads from backend). */
function normalizeModelUrl(url: string | null | undefined): string {
  if (url == null || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
}

export type Model3DOption = {
  label: string;
  modelUrl: string;
  image?: string | null;
};

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          alt?: string;
          "camera-controls"?: boolean;
          style?: React.CSSProperties;
        },
        HTMLElement
      >;
    }
  }
}

function getTitleLabel(item: any, field = "title", lang = "en") {
  const list = item?.titleLocalizations?.filter((e: any) => e.field === field) || [];
  const match =
    list.find((e: any) => e.language === lang) ||
    list.find((e: any) => e.language === "en") ||
    list[0];
  return match?.value || item?.title || "";
}

const MODEL_API_LOAD = [
  { apiType: "three_d_model" },
  { apiType: "emb_3d_model" },
] as const;

function build3DEntries(
  titles: any[],
  lang: string,
  sourceLabel?: string
): Model3DOption[] {
  const entries: Model3DOption[] = [];
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title", lang);
    (tile.subtitles || []).forEach((sub: any) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title", lang);
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const modelUrl = file?.url;
      if (!modelUrl) return;
      const name = `${tileLabel} / ${slideLabel}`;
      entries.push({
        label: sourceLabel ? `${sourceLabel}: ${name}` : name,
        modelUrl,
        image: sub?.image ?? null,
      });
    });
  });
  return entries;
}

const Model3DElement = (props: ICanvasComponent) => {
  const { content, id, cameraOrbit: savedCameraOrbit, isPlayMode, cameraViews } = props;
  const { actions, playback } = useContext(CanvasContext);
  const { t } = useTranslation();
  const lang = useSelector((state: any) => state.general?.lang || "en");
  const views = Array.isArray(cameraViews) ? cameraViews : [];
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeReadyRef = useRef(false);
  const pendingCameraRef = useRef<any>(null);
  const viewsRef = useRef(views);
  viewsRef.current = views;
  const [controller] = useMaterialUIController();
  const isDark = controller?.darkMode === true;
  const viewerRef = useRef<HTMLElement | null>(null);
  const saveCameraTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modelOptions, setModelOptions] = useState<Model3DOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Model3DOption | null>(null);

  const modelUrl =
    typeof content === "string" ? content : (content as any)?.url || "";
  const displayUrl = normalizeModelUrl(modelUrl);

  useEffect(() => {
    if (isPlayMode) return;
    let cancelled = false;
    setLoading(true);
    setModelOptions([]);
    Promise.all(
      MODEL_API_LOAD.map(({ apiType }) => GetTitlesCallNoStore(lang, apiType, false))
    )
      .then((results) => {
        if (cancelled) return;
        const next: Model3DOption[] = [];
        MODEL_API_LOAD.forEach(({ apiType }, i) => {
          const list = Array.isArray(results[i]) ? results[i] : [];
          const sourceLabel =
            apiType === "emb_3d_model"
              ? t("pages.presentationMaker.catalogueEmbryology")
              : t("pages.presentationMaker.catalogueHistology");
          next.push(...build3DEntries(list, lang, sourceLabel));
        });
        setModelOptions(next);
      })
      .catch(() => {
        if (!cancelled) setModelOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPlayMode, lang, t]);

  useEffect(() => {
    if (!displayUrl) return;
    const viewer = viewerRef.current;
    if (!viewer || !savedCameraOrbit) return;
    const el = viewer as unknown as { cameraOrbit?: string };
    if (typeof el.cameraOrbit !== "undefined") {
      el.cameraOrbit = savedCameraOrbit;
    }
  }, [displayUrl, savedCameraOrbit]);

  const handleSelectModel = (option: Model3DOption | null) => {
    if (option) {
      actions?.updateCanvasData({ id, content: option.modelUrl });
      setSelectedOption(option);
      actions?.saveNow?.();
    }
  };

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const url = e.target?.value?.trim();
    if (url) {
      actions?.updateCanvasData({ id, content: url });
      actions?.saveNow?.();
    }
  };

  const postToViewer = (message: any) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage({ target: "model-viewer", ...message }, "*");
  };

  // Listen for the iframe's ready + camera reports (saved viewpoints).
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data: any = event.data;
      if (!data || data.source !== "model-viewer") return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (data.type === "ready") {
        iframeReadyRef.current = true;
        if (pendingCameraRef.current) {
          postToViewer(pendingCameraRef.current);
          pendingCameraRef.current = null;
        }
      }
      if (data.type === "camera" && data.orbit && id && !isPlayMode) {
        const current = viewsRef.current;
        const view = {
          id: `view_${Date.now()}`,
          label: t("pages.presentationMaker.viewLabel", {
            n: current.length + 1,
          }),
          orbit: data.orbit,
          target: data.target || undefined,
          fov: data.fov || undefined,
        };
        actions?.updateCanvasData({ id, cameraViews: [...current, view] });
        actions?.saveNow?.();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [id, isPlayMode, t]);

  // Presentation mode: each click moves the camera to the next saved viewpoint.
  const focusStart = id ? playback?.focusStart?.get(id) : undefined;
  const localStep =
    !isPlayMode || focusStart == null
      ? null
      : playback?.revealAll
        ? views.length - 1
        : playback?.active
          ? (playback.step ?? 0) - focusStart
          : -1;
  useEffect(() => {
    if (localStep == null || !views.length) return;
    const index = Math.min(localStep, views.length - 1);
    const jump = !playback?.active || Boolean(playback?.instant) || prefersReducedMotion();
    const message =
      index >= 0
        ? { type: "set-camera", orbit: views[index].orbit, target: views[index].target, fov: views[index].fov, jump }
        : { type: "reset-view" };
    if (iframeReadyRef.current) postToViewer(message);
    else pendingCameraRef.current = message;
  }, [localStep, playback?.active, playback?.token]);

  const saveViewpoint = () => {
    postToViewer({ type: "get-camera", requestId: `${id}_${Date.now()}` });
  };

  const clearViewpoints = () => {
    if (!id) return;
    actions?.updateCanvasData({ id, cameraViews: [] });
    actions?.saveNow?.();
  };

  const handleCameraChange = () => {
    const viewer = viewerRef.current as unknown as { cameraOrbit?: string } | null;
    if (!viewer?.cameraOrbit || !id) return;
    if (saveCameraTimeoutRef.current) clearTimeout(saveCameraTimeoutRef.current);
    saveCameraTimeoutRef.current = setTimeout(() => {
      saveCameraTimeoutRef.current = null;
      const orbit = (viewerRef.current as unknown as { cameraOrbit?: string })?.cameraOrbit;
      if (orbit) {
        actions?.updateCanvasData({ id, cameraOrbit: orbit });
        actions?.saveNow?.();
      }
    }, 400);
  };

  if (!modelUrl) {
    if (isPlayMode) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: isDark ? "#020617" : "#e5e7eb",
            borderRadius: 6,
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#e8e8e8",
          padding: 8,
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: 12, marginBottom: 6, display: "block" }}>
          {t("pages.presentationMaker.searchSelectModel")}
        </span>
        <Autocomplete
          size="small"
          fullWidth
          loading={loading}
          options={modelOptions}
          value={selectedOption}
          onChange={(_, value) => handleSelectModel(value)}
          getOptionLabel={(opt) => opt?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("pages.presentationMaker.searchByName")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">🔍</InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.label}-${opt.modelUrl}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: "grey.200",
                  }}
                >
                  {opt.image ? (
                    <Box
                      component="img"
                      src={opt.image}
                      alt={t("pages.presentationMaker.model3dTitle")}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    /* @ts-ignore */
                    <model-viewer
                      src={opt.modelUrl}
                      alt={t("pages.presentationMaker.model3dTitle")}
                      camera-controls
                      style={{ width: "100%", height: "100%", minWidth: 40, minHeight: 40 }}
                    />
                  )}
                </Box>
                <span style={{ fontSize: 13 }}>{opt.label}</span>
              </Box>
            </li>
          )}
        />
        <span style={{ fontSize: 11, marginTop: 8, marginBottom: 4 }}>
          {t("pages.presentationMaker.orPasteModelUrl")}
        </span>
        <input
          type="url"
          placeholder=".glb / .gltf URL"
          style={{ width: "100%", padding: 6, fontSize: 12, boxSizing: "border-box" }}
          onBlur={handleUrlBlur}
        />
      </div>
    );
  }

  // Use iframe (same as QuizSystem, layouts/titles) so model-viewer loads reliably
  const themeParam = isDark ? "dark" : "light";
  const iframeSrc =
    typeof window !== "undefined"
      ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(displayUrl)}&theme=${themeParam}`
      : "";

  const viewButtonSx = {
    width: 30,
    height: 30,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: 2,
    "&:hover": { bgcolor: "action.hover" },
    "& .MuiSvgIcon-root": { fontSize: 17 },
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 200,
        overflow: "hidden",
        background: isDark ? "#1a1a1a" : "#f5f5f5",
      }}
    >
      {!isPlayMode ? (
        <Box sx={{ position: "absolute", top: 10, left: 10, zIndex: 2, display: "flex", gap: 0.5 }}>
          <Tooltip
            title={t("pages.presentationMaker.saveViewpoint", {
              count: views.length,
            })}
          >
            <IconButton
              size="small"
              onClick={saveViewpoint}
              aria-label={t("pages.presentationMaker.saveViewpoint", {
                count: views.length,
              })}
              sx={{
                ...viewButtonSx,
                ...(views.length ? { bgcolor: "#0ea5e9", color: "#fff", "&:hover": { bgcolor: "#0284c7" } } : null),
              }}
            >
              <CameraAltOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {views.length ? (
            <Tooltip title={t("pages.presentationMaker.clearViewpoints")}>
              <IconButton
                size="small"
                onClick={clearViewpoints}
                aria-label={t("pages.presentationMaker.clearViewpoints")}
                sx={viewButtonSx}
              >
                <LayersClearOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
      <iframe
        ref={iframeRef}
        title={t("pages.presentationMaker.model3dTitle")}
        src={iframeSrc}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 200,
          border: "none",
          display: "block",
        }}
      />
    </div>
  );
};

export default Model3DElement;
